import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Boxes, Building2, Package } from 'lucide-react';
import { inventarioApi } from '@/services/inventarioApi';
import { formatDate } from '@/utils/formatters';

const TIPO_MOV_LABELS: Record<string, string> = {
  ENTRADA: 'Entrada',
  SALIDA: 'Salida',
  TRASLADO: 'Traslado',
  AJUSTE: 'Ajuste',
};

const TIPO_MOV_COLORS: Record<string, string> = {
  ENTRADA: 'bg-emerald-100 text-emerald-700',
  SALIDA: 'bg-red-100 text-red-700',
  TRASLADO: 'bg-blue-100 text-blue-700',
  AJUSTE: 'bg-amber-100 text-amber-700',
};

export function DashboardInventario() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['inventario-dashboard'],
    queryFn: inventarioApi.dashboard,
  });

  if (isLoading) {
    return <p className="text-center py-12 text-gray-500">Cargando dashboard...</p>;
  }

  if (error || !data) {
    return <p className="text-center py-12 text-red-500">Error al cargar el dashboard.</p>;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Kpi icon={Package} label="Ítems activos" value={data.totalItems} color="blue" />
        <Kpi icon={Building2} label="Almacenes" value={data.totalAlmacenes} color="purple" />
        <Kpi icon={Boxes} label="Total unidades" value={data.totalUnidades} color="emerald" />
        <Kpi icon={AlertTriangle} label="Stock bajo" value={data.itemsStockBajo} color="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-4 sm:px-6 py-3 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Stock por almacén</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {data.stockPorAlmacen.length === 0 ? (
              <p className="text-center py-8 text-gray-500 text-sm">Sin almacenes activos</p>
            ) : (
              data.stockPorAlmacen.map((s) => (
                <div key={s.almacenId} className="flex items-center justify-between px-4 sm:px-6 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{s.almacenNombre}</p>
                    <p className="text-xs text-gray-500 font-mono">{s.almacenCodigo}</p>
                  </div>
                  <span className="text-lg font-bold text-gray-900">{s.cantidad}<span className="text-xs text-gray-500 font-normal ml-1">ud</span></span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-4 sm:px-6 py-3 border-b border-gray-200 flex items-center gap-2">
            <AlertTriangle size={16} className="text-amber-500" />
            <h3 className="font-semibold text-gray-900">Alertas de stock bajo</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {data.alertasStockBajo.length === 0 ? (
              <p className="text-center py-8 text-gray-500 text-sm">Sin alertas activas</p>
            ) : (
              data.alertasStockBajo.map((row) => (
                <div key={row.stockId} className="flex items-center justify-between px-4 sm:px-6 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{row.item.nombre}</p>
                    <p className="text-xs text-gray-500">
                      {row.almacen.nombre}{row.item.codigo ? ` · ${row.item.codigo}` : ''}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-red-600 shrink-0 ml-3">
                    {row.cantidad}/{row.umbralStockBajo}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-4 sm:px-6 py-3 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Movimientos recientes</h3>
        </div>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Fecha</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Tipo</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Cantidad</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Origen</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Destino</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Usuario</th>
              </tr>
            </thead>
            <tbody>
              {data.movimientosRecientes.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8 text-gray-500">Sin movimientos recientes</td></tr>
              ) : (
                data.movimientosRecientes.map((m) => (
                  <tr key={m.movimientoId} className="border-b border-gray-100">
                    <td className="px-4 py-2.5 text-sm text-gray-600">{formatDate(m.fecha)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TIPO_MOV_COLORS[m.tipo]}`}>
                        {TIPO_MOV_LABELS[m.tipo]}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-sm font-medium">{m.cantidad}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">{m.almacenOrigen?.nombre ?? '—'}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">{m.almacenDestino?.nombre ?? '—'}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">{m.usuarioNombre}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="md:hidden divide-y divide-gray-100">
          {data.movimientosRecientes.length === 0 ? (
            <p className="text-center py-8 text-gray-500 text-sm">Sin movimientos recientes</p>
          ) : (
            data.movimientosRecientes.map((m) => (
              <div key={m.movimientoId} className="px-4 py-3">
                <div className="flex items-center justify-between mb-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TIPO_MOV_COLORS[m.tipo]}`}>
                    {TIPO_MOV_LABELS[m.tipo]}
                  </span>
                  <span className="text-sm font-semibold">{m.cantidad} ud</span>
                </div>
                <p className="text-xs text-gray-500">
                  {m.almacenOrigen?.nombre ?? '—'} → {m.almacenDestino?.nombre ?? '—'}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">{formatDate(m.fecha)} · {m.usuarioNombre}</p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

interface KpiProps {
  icon: typeof Package;
  label: string;
  value: number;
  color: 'blue' | 'purple' | 'emerald' | 'amber';
}

function Kpi({ icon: Icon, label, value, color }: KpiProps) {
  const palette = {
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
  }[color];
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${palette}`}>
          <Icon size={18} />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-gray-500 truncate">{label}</p>
          <p className="text-xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
}
